import { computed, inject, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FetchPolicy } from '@apollo/client/core';
import { TranslateService } from '@ngx-translate/core';
import { Apollo } from 'apollo-angular';
import { map } from 'rxjs';
import { localisedCategoryName, NewsCategory } from './models/news-category';
import { Queries } from '../queries';

/**
 * The news categories, shared by the news page and the CMS. The API returns them unordered; they
 * are sorted here alphabetically by Dutch name in every language, as they always were.
 */
@Injectable({
  providedIn: 'root'
})
export class NewsCategoryService {
  private apollo = inject(Apollo);
  private translate = inject(TranslateService);

  private readonly language = toSignal(
    this.translate.onLangChange.pipe(map(event => event.lang)),
    { initialValue: this.translate.getCurrentLang() });

  private readonly loadedCategories = signal<NewsCategory[]>([]);

  readonly loading = signal<boolean>(false);
  readonly error = signal<boolean>(false);

  readonly categories = computed(() =>
    [...this.loadedCategories()].sort((a, b) =>
      a.name.nl.localeCompare(b.name.nl, 'nl', { sensitivity: 'base' })));

  /**
   * Loads the categories. Call with `network-only` after a category changed, so the stale list
   * in the Apollo cache is not served again.
   */
  load(fetchPolicy: FetchPolicy = 'cache-first'): void {
    this.loading.set(true);
    this.error.set(false);

    this.apollo.query<{ newsCategories: NewsCategory[] }>({
      query: Queries.GetNewsCategories,
      fetchPolicy
    }).subscribe({
      next: result => {
        this.loadedCategories.set(result.data?.newsCategories ?? []);
        this.loading.set(false);
      },
      error: error => {
        console.error('Failed to load the news categories', error);
        this.error.set(true);
        this.loading.set(false);
      }
    });
  }

  categoryName(category: NewsCategory | null | undefined): string {
    return category ? localisedCategoryName(category, this.language()) : '';
  }
}
